<template>
  <div>
    <v-card class="pa-4 ma-3 main-card">
      <div class="my-1 mx-3">
        <div class="d-flex align-end">
          <div class="icon-back">
            <v-icon id="icon" size="37" color="white">
              mdi-chart-areaspline
            </v-icon>
          </div>

          <v-toolbar-title class="main-title">
            {{ $t('Cronograma') }}
          </v-toolbar-title>

          <div class="ml-6 color-container">
            <div class="d-flex align-end">
              <div class="executed-ondate-color"></div>
              {{ $t('Agendada') }}: {{ statistics.scheduled || 0 }}
            </div>
            <div class="d-flex align-end">
              <div class="schedule-color"></div>
              {{ $t('Executada dentro do prazo') }}:
              {{ statistics.executedInTime || 0 }}
            </div>
            <div class="d-flex align-end">
              <div class="executed-offdate-color"></div>
              {{ $t('Executada fora do prazo') }}:
              {{ statistics.executedLate || 0 }}
            </div>
            <div class="d-flex align-end">
              <div class="executed-outside-month-color"></div>
              {{ $t('Atrasada') }}: {{ statistics.late || 0 }}
            </div>
            <div class="d-flex align-end">
              <div class="virtualized-color"></div>
              {{ $t('Virtualizadas') }}: {{ statistics.virtualized || 0 }}
            </div>
          </div>
        </div>
      </div>

      <div
        class="d-flex justify-end align-center mx-3 mb-2"
        style="min-height: 32px"
      >
        <v-btn
          v-if="hiddenColumnsList.length > 0"
          small
          depressed
          color="#0F3565"
          dark
          @click="showHiddenModal = true"
        >
          <v-icon small left>mdi-eye-off</v-icon>
          {{ hiddenColumnsList.length }} {{ $t('coluna(s) oculta(s)') }}
        </v-btn>
      </div>
      <hidden-columns-modal
        v-model="showHiddenModal"
        :hidden-columns="hiddenColumnsList"
        @show-column="showColumn"
        @show-all="showAllColumns"
      />

      <v-simple-table class="mt-2 custom-table mx-3 mb-6" fixed-header>
        <template v-slot:default>
          <thead>
            <tr>
              <th
                v-for="(col, idx) in columns"
                :key="col.label"
                :class="`text-${col.align} resizable-th`"
                v-show="visibleColumns[idx]"
              >
                {{ $t(col.label)
                }}<span class="col-hide-btn" @click.stop="hideColumn(idx)"
                  ><v-icon x-small color="rgba(255,255,255,0.9)"
                    >mdi-eye-off-outline</v-icon
                  ></span
                >
                <div
                  class="col-resize-handle"
                  @mousedown.stop.prevent="startResize(idx, $event)"
                ></div>
              </th>
              <th
                v-for="(name, idx) in monthNames"
                :key="name"
                class="text-center resizable-th"
                v-show="visibleColumns[14 + idx]"
              >
                {{ $t(name)
                }}<span class="col-hide-btn" @click.stop="hideColumn(14 + idx)"
                  ><v-icon x-small color="rgba(255,255,255,0.9)"
                    >mdi-eye-off-outline</v-icon
                  ></span
                >
                <div
                  class="col-resize-handle"
                  @mousedown.stop.prevent="startResize(14 + idx, $event)"
                ></div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in filteredRows" :key="index">
              <td v-show="visibleColumns[0]">{{ item.institution }}</td>
              <td v-show="visibleColumns[1]">
                {{ item.identification }}
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      text
                      color="#0F3565"
                      :href="
                        item.openOrderId
                          ? `/editServiceOrder/${item.openOrderId}`
                          : ''
                      "
                      target="_blank"
                      v-bind="attrs"
                      v-on="on"
                      v-if="item.openOrderId"
                    >
                      <v-icon> mdi-exclamation-thick </v-icon>
                    </v-btn>
                  </template>
                  <span>Em manutenção corretiva! Clique para acessar</span>
                </v-tooltip>
              </td>
              <td v-show="visibleColumns[2]">{{ item.propertyCode }}</td>
              <td v-show="visibleColumns[3]">{{ item.serialNumber }}</td>
              <td v-show="visibleColumns[4]">{{ item.equipmentName }}</td>
              <td v-show="visibleColumns[5]">{{ item.manufacturer }}</td>
              <td v-show="visibleColumns[6]">{{ item.model }}</td>
              <td v-show="visibleColumns[7]">{{ item.workshop }}</td>
              <td v-show="visibleColumns[8]">{{ item.unidade }}</td>
              <td v-show="visibleColumns[9]">{{ item.departmentName }}</td>
              <td class="text-center" v-show="visibleColumns[10]">
                {{ $t(item.serviceOrderType) }}
              </td>
              <td class="text-lef" v-show="visibleColumns[11]">
                {{ $t(item.responsability) }}
              </td>
              <td class="text-left" v-show="visibleColumns[12]">
                {{ item.responsible }}
              </td>
              <td class="text-left" v-show="visibleColumns[13]">
                {{ item.periodicity }}
              </td>
              <td
                class="text-center"
                v-for="month in 12"
                :key="month"
                v-show="visibleColumns[13 + month]"
              >
                <template v-if="getMonthData(item, month).length">
                  <v-btn
                    v-if="
                      getMonthData(item, month).length === 1 &&
                      !getMonthData(item, month)[0].isVirtualized
                    "
                    height="41"
                    small
                    depressed
                    :color="
                      getMonthData(item, month)[0].backgroundColor ||
                      'transparent'
                    "
                    :href="
                      getMonthUrl(item, month, getMonthData(item, month)[0])
                    "
                    target="_blank"
                  >
                    {{ getMonthData(item, month)[0].displayDate }}
                  </v-btn>

                  <v-btn
                    v-else-if="
                      getMonthData(item, month).length === 1 &&
                      getMonthData(item, month)[0].isVirtualized
                    "
                    height="41"
                    small
                    depressed
                    disabled
                    class="virtualized-button"
                  >
                    {{ getMonthData(item, month)[0].displayDate }}
                  </v-btn>

                  <v-btn
                    v-else
                    height="41"
                    small
                    depressed
                    :color="getMonthButtonColor(item, month)"
                    :href="getMonthListUrl(item, month)"
                    target="_blank"
                  >
                    {{ getMonthData(item, month)[0].displayDate }}
                    +
                  </v-btn>
                </template>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>
  </div>
</template>

<script>
import ApiService from '../../../services/ApiService'
import HiddenColumnsModal from './HiddenColumnsModal.vue'
export default {
  name: 'Schedule',
  components: { HiddenColumnsModal },
  created() {
    const filters = JSON.parse(localStorage.getItem('scheduleFilters'))
    this.list(filters)
  },
  mounted() {
    this._tableFixed = false
    this._resizingActive = false

    setTimeout(() => {
      const slider = document.querySelector(
        '.custom-table .v-data-table__wrapper'
      )
      let isDown = false
      let startX
      let scrollLeft
      slider.addEventListener('mousedown', (e) => {
        if (e.target.closest('.col-resize-handle')) return
        isDown = true
        slider.classList.add('active')
        startX = e.pageX - slider.offsetLeft
        scrollLeft = slider.scrollLeft
      })
      slider.addEventListener('mouseleave', () => {
        isDown = false
        slider.classList.remove('active')
      })
      slider.addEventListener('mouseup', () => {
        isDown = false
        slider.classList.remove('active')
      })
      slider.addEventListener('mousemove', (e) => {
        if (!isDown || this._resizingActive) return
        e.preventDefault()
        const x = e.pageX - slider.offsetLeft
        const walk = (x - startX) * 3
        slider.scrollLeft = scrollLeft - walk
      })
    }, 2000)

    this._resizeMouseMove = (e) => {
      if (!this._resizingActive || !this.resizingState) return
      e.preventDefault()
      const { th, tbl, ths, startX, startWidth } = this.resizingState
      const newWidth = Math.max(8, startWidth + (e.clientX - startX))
      th.style.width = newWidth + 'px'
      const total = ths.reduce(
        (sum, t) => sum + (parseInt(t.style.width) || 0),
        0
      )
      tbl.style.width = total + 'px'
    }
    this._resizeMouseUp = () => {
      this._resizingActive = false
      this.resizingState = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', this._resizeMouseMove)
    document.addEventListener('mouseup', this._resizeMouseUp)
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this._resizeMouseMove)
    document.removeEventListener('mouseup', this._resizeMouseUp)
  },
  data() {
    return {
      statistics: {},
      apiService: new ApiService(),
      rows: [],
      year: null,
      monthNames: [
        'Jan',
        'Fev',
        'Mar',
        'Abr',
        'Mai',
        'Jun',
        'Jul',
        'Ago',
        'Set',
        'Out',
        'Nov',
        'Dez',
      ],
      columns: [
        { label: 'Instituição', align: 'left' },
        { label: 'Identificação', align: 'left' },
        { label: 'Patrimônio', align: 'left' },
        { label: 'Nº Série', align: 'left' },
        { label: 'Equipamento', align: 'left' },
        { label: 'Fabricante', align: 'left' },
        { label: 'Modelo', align: 'left' },
        { label: 'Oficina', align: 'left' },
        { label: 'Unidade', align: 'left' },
        { label: 'Setor', align: 'left' },
        { label: 'Tipo OS', align: 'center' },
        { label: 'Resp.', align: 'left' },
        { label: 'Responsável', align: 'left' },
        { label: 'Periodicidade', align: 'left' },
      ],
      visibleColumns: Array(26).fill(true),
      showHiddenModal: false,
      resizingState: null,
    }
  },
  computed: {
    filteredRows() {
      if (this.visibleColumns.slice(14).every(Boolean)) return this.rows
      return this.rows.filter((item) =>
        this.visibleColumns.slice(14).some((visible, idx) => {
          if (!visible) return false
          const m = item.monthlyData.find((x) => x.month === idx + 1)
          return m && m.serviceOrders.length > 0
        })
      )
    },
    hiddenColumnsList() {
      const labels = [...this.columns.map((c) => c.label), ...this.monthNames]
      return this.visibleColumns
        .map((visible, idx) => ({ visible, idx, label: labels[idx] }))
        .filter(({ visible }) => !visible)
    },
  },
  methods: {
    startResize(colIdx, event) {
      event.preventDefault()
      event.stopPropagation()

      const ths = Array.from(this.$el.querySelectorAll('thead th'))
      const th = ths[colIdx]
      if (!th) return

      const tbl = this.$el.querySelector('table')
      if (!tbl) return

      if (!this._tableFixed) {
        const totalWidth = ths.reduce((sum, t) => sum + t.offsetWidth, 0)
        ths.forEach((t) => {
          t.style.width = t.offsetWidth + 'px'
        })
        tbl.style.tableLayout = 'fixed'
        tbl.style.width = totalWidth + 'px'
        tbl.classList.add('resize-active')
        this._tableFixed = true
      }

      this._resizingActive = true
      this.resizingState = {
        th,
        tbl,
        ths,
        startX: event.clientX,
        startWidth: parseInt(th.style.width) || th.offsetWidth,
      }
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    hideColumn(idx) {
      this.$set(this.visibleColumns, idx, false)
    },
    showColumn(idx) {
      this.$set(this.visibleColumns, idx, true)
      this.resetTableLayout()
    },
    showAllColumns() {
      this.visibleColumns = Array(26).fill(true)
      this.resetTableLayout()
    },
    resetTableLayout() {
      this.$nextTick(() => {
        const tbl = this.$el?.querySelector('table')
        if (!tbl) return
        tbl.querySelectorAll('thead th').forEach((th) => {
          th.style.width = ''
        })
        tbl.style.tableLayout = ''
        tbl.style.width = ''
        tbl.classList.remove('resize-active')
        this._tableFixed = false
      })
    },
    getMonthData(item, month) {
      const m = item.monthlyData.find((x) => x.month === month)
      return m ? m.serviceOrders : []
    },
    getMonthButtonColor(item, month) {
      const orders = this.getMonthData(item, month)
      const priorityColors = ['#ff4f64', '#3c8ea6', '#ffd21f']
      for (const color of priorityColors) {
        if (orders.some((o) => o.backgroundColor?.toLowerCase() === color))
          return color
      }
      return '#b9ce1e'
    },
    getMonthUrl(item, month, order) {
      if (order.isPlanning) {
        const info = item.planningOSInfo || {
          responsability: item.responsability,
        }
        localStorage.setItem(`planningOSInfo-${order.id}`, JSON.stringify(info))
        return `/newServiceOrder?equipmentId=${item.equipmentId}&month=${month}&year=${this.year}&type=${item.serviceOrderType}&fromPlanning=true&scheduledDate=${order.date}&planningId=${order.id}`
      }
      return `/editServiceOrder/${order.id}`
    },
    getMonthListUrl(item, month) {
      if (!item.equipmentId && item.departmentId) {
        return `/listServiceOrders?departmentId=${item.departmentId}&month=${month}&year=${this.year}&type=${item.serviceOrderType}`
      }
      return `/listServiceOrders?equipment=${item.equipmentId}&month=${month}&year=${this.year}&type=${item.serviceOrderType}`
    },
    async list(filters) {
      try {
        const { content } = await this.apiService.post('Chronogram', filters)
        this.rows = content.rows
        this.year = content.year
        this.statistics = content.statistics
      } catch {}
    },
  },
}
</script>

<style>
.v-data-table__wrapper::-webkit-scrollbar {
  background-color: #fff !important;
  opacity: 0.6 !important;
  height: 35px;
  width: 35px;
}

.v-data-table__wrapper::-webkit-scrollbar-thumb {
  background-color: #ccc !important;
  opacity: 0.8 !important;
  border: 14px solid #fff !important;
  height: 5px !important;
  border-radius: 20px;
}

.v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background-color: #888 !important;
  opacity: 0.6 !important;
}

.custom-table table {
  min-width: 100%;
}

.custom-table table.resize-active th,
.custom-table table.resize-active td {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.resizable-th {
  position: relative;
}

.col-resize-handle {
  position: absolute;
  right: -4px;
  top: 0;
  bottom: 0;
  width: 9px;
  cursor: col-resize;
  background: transparent;
  z-index: 10;
  pointer-events: all;
}

.col-resize-handle::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 20%;
  bottom: 20%;
  width: 2px;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.4);
  border-radius: 2px;
}

.col-resize-handle:hover::after {
  background: rgba(255, 255, 255, 0.85);
}

.resizable-th .col-hide-btn {
  display: none;
  position: absolute;
  top: 3px;
  left: 4px;
  cursor: pointer;
  z-index: 5;
  opacity: 0.85;
  line-height: 1;
}

.resizable-th:hover .col-hide-btn {
  display: inline-flex;
  align-items: center;
}
</style>

<style scoped>
.schedule-color,
.executed-ondate-color,
.executed-offdate-color,
.executed-outside-month-color,
.virtualized-color {
  width: 60px;
  height: 20px;
  padding: 6px;
  margin: 1px 6px;
  border: 2px solid #ccc;
  border-radius: 4px;
}

.color-container {
  font-size: 12px;
  font-weight: 700;
}

.schedule-color {
  background: #b9ce1e;
}

.executed-ondate-color {
  background: #ffd21f;
}

.executed-offdate-color {
  background: #3c8ea6;
}

.executed-outside-month-color {
  background: #ff4f64;
}

.virtualized-color {
  background: repeating-linear-gradient(
    45deg,
    #cccccc,
    #cccccc 3px,
    #c2c2c2 3px,
    #c2c2c2 6px
  ) !important;
  opacity: 0.6 !important;
}

.custom-table thead tr th {
  padding-left: 16px !important;
  background: #2a8ea4 !important;
  font-weight: 500 !important;
  font-size: 12px !important;
  line-height: 16px !important;
  color: #ffffff !important;
  min-width: 90px;
}

.custom-table thead tr th:first-child {
  border-radius: 4px 0 0 4px !important;
}

.custom-table thead tr th:last-child {
  padding-right: 16px !important;
  border-radius: 0 4px 4px 0 !important;
}

.custom-table tbody tr td:last-child {
  padding-right: 16px !important;
  border-radius: 0 4px 4px 0 !important;
}

.custom-table tbody tr td {
  padding: 4px 0 4px 14px !important;
  font-weight: 300 !important;
  font-size: 12px !important;
  line-height: 15px !important;
  letter-spacing: 0.15px !important;
  color: #808080 !important;
}

.custom-table tbody tr td .v-btn {
  color: #202020 !important;
}

.main-card {
  z-index: 16 !important;
  background: #ffffff;
  box-shadow: 0px 5px 16px rgba(0, 0, 0, 0.05) !important;
  border-radius: 8px;
}

.icon-back {
  width: 60px;
  height: 54px;
  background: #b9ce1e;
  border-radius: 7px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.main-title {
  margin-left: 10px;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 27px;

  color: #000000;
}
.virtualized-button {
  background: repeating-linear-gradient(
    45deg,
    #cccccc,
    #cccccc 3px,
    #c2c2c2 3px,
    #c2c2c2 6px
  ) !important;
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

.virtualized-button:hover {
  background: repeating-linear-gradient(
    45deg,
    #cccccc,
    #cccccc 3px,
    #c2c2c2 3px,
    #c2c2c2 6px
  ) !important;
}

.virtualized-button .v-btn__content {
  color: #808080 !important;
}
</style>
